import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import type { QuotaIncreaseRequestOutcome } from "@starsach/shared-types";
import { QUOTA_REQUEST_STATUS as STATUS } from "../../lib/quota-requests";
import { useCheckQuotaIncreaseRequest, useQuotaIncreaseRequests, useQuotas, useRequestQuotaIncrease } from "../../api/hooks";
import { Hint } from "../../components/Hint";
import { Card, DataTable, Dialog, EmptyState, ErrorState, LoadingState, RelativeTime, StatusPill, btnPrimary, btnSecondary, inputClass } from "../../components/ui";

const SOURCE_LABEL: Record<string, string> = {
  resource_count: "counted",
  cloudwatch_usage: "CloudWatch usage",
  native_metric: "native metric",
  unavailable: "no usage signal",
  rate: "a rate limit - cache or queue, never increase",
};


/** The Support API is not readable on Basic support, so the case cannot be shown here; Support Center in the customer's own account can. */
function CaseLink({ caseId }: { caseId: string | undefined }) {
  if (!caseId) return null;
  return (
    <a href={`https://support.console.aws.amazon.com/support/home#/case/?displayId=${encodeURIComponent(caseId)}`} target="_blank" rel="noreferrer" className="text-accent-strong underline dark:text-accent">
      case {caseId}
    </a>
  );
}

/** design.md Phase 6: every row says how its usage was measured, so a count is never read as a percentage. */
export function QuotasTab({ environmentId, canRequestIncrease }: { environmentId: string; canRequestIncrease: boolean }) {
  const q = useQuotas(environmentId);
  const requests = useQuotaIncreaseRequests(environmentId, canRequestIncrease);
  const check = useCheckQuotaIncreaseRequest(environmentId);
  const [requesting, setRequesting] = useState<{ serviceCode: string; quotaCode: string; quotaName: string; value: number } | null>(null);
  if (q.isPending) return <LoadingState />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const showAction = canRequestIncrease && q.data.increaseRequestsAvailable;
  // Within 80% of the limit is the threshold the Focus page uses too.
  const recommended = q.data.quotas.filter((r) => (r.usage_percent ?? 0) >= 80);
  // One open request per quota (AWS's rule, and the API's): while it is open the row shows it instead of the button.
  const openByQuota = new Map((requests.data?.requests ?? []).filter((r) => STATUS[r.status].open).map((r) => [`${r.service_code}#${r.quota_code}`, r]));
  const requestButton = (r: (typeof q.data.quotas)[number]) => {
    const open = openByQuota.get(`${r.service_code}#${r.quota_code}`);
    if (showAction && open) {
      const checking = check.isPending && check.variables === open.aws_request_id;
      return (
        <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs text-neutral-600 dark:text-neutral-400">
          <StatusPill status={STATUS[open.status].label} tone={STATUS[open.status].tone} />
          <span title={`Requested by ${open.requested_by}. AWS allows one open request per quota; it can be requested again once this one closes.`}>
            <RelativeTime iso={open.requested_at} />
          </span>
          <CaseLink caseId={open.case_id} />
          <button
            type="button"
            onClick={() => check.mutate(open.aws_request_id)}
            disabled={checking}
            aria-label={`Check status of the increase request for ${r.quota_name}`}
            className="inline-flex min-h-8 items-center rounded-lg border border-neutral-300 px-2.5 text-xs font-medium hover:bg-neutral-100 disabled:opacity-50 dark:border-neutral-700 dark:hover:bg-neutral-800"
          >
            {checking ? "Checking…" : "Check status"}
          </button>
        </span>
      );
    }
    return showAction && r.adjustable ? (
      <button type="button" onClick={() => setRequesting({ serviceCode: r.service_code, quotaCode: r.quota_code, quotaName: r.quota_name, value: r.value })} className="inline-flex min-h-8 items-center rounded-lg border border-neutral-300 px-2.5 text-xs font-medium hover:bg-neutral-100 dark:border-neutral-700 dark:hover:bg-neutral-800">
        Request increase
      </button>
    ) : showAction ? (
      <span className="text-xs text-neutral-500" title="AWS does not allow self-service increases for this quota">not adjustable</span>
    ) : null;
  };

  return (
    <div className="grid gap-6">
      {requesting && <RequestIncreaseForm environmentId={environmentId} quota={requesting} onClose={() => setRequesting(null)} />}
      <Card title={<>Recommended actions<Hint id="quotas.recommended" label="recommended actions" /></>}>
        <DataTable
          caption="Quotas within 80% of their limit"
          rows={recommended}
          rowKey={(r) => `${r.service_code}#${r.quota_code}`}
          filter={false}
          sortBy={(r) => -(r.usage_percent ?? 0)}
          empty={<p className="py-3 text-sm text-neutral-600 dark:text-neutral-400">No recommended actions - nothing is within 80% of its limit.</p>}
          columns={[
            { key: "name", header: "Quota", render: (r) => <span>{r.quota_name} <span className="ml-1 font-mono text-xs text-neutral-500">{r.service_code}</span></span> },
            { key: "usage", header: "Usage", numeric: true, render: (r) => `${r.usage ?? "-"} / ${r.value}` },
            { key: "pct", header: "%", numeric: true, render: (r) => <span className="font-semibold text-amber-700 dark:text-amber-300">{(r.usage_percent ?? 0).toFixed(0)}%</span> },
            { key: "action", header: "", render: requestButton },
          ]}
        />
      </Card>
      {canRequestIncrease && (
        <Card title={<>Increase requests<Hint id="quotas.increase-requests" label="increase requests" /></>}>
          {!q.data.increaseRequestsAvailable && <p className="text-sm text-neutral-600 dark:text-neutral-400">This customer has not opted into quota increase requests (no QuotaIncreaseRequestRole in their stack).</p>}
          {requests.data && (
            <DataTable
              caption="Quota increase requests made from the portal"
              rows={requests.data.requests}
              rowKey={(r) => r.aws_request_id}
              empty={<EmptyState title="No increase requests yet" />}
              columns={[
                { key: "quota", header: "Quota", render: (r) => r.quota_name },
                { key: "value", header: "Requested", numeric: true, render: (r) => `${r.previous_value ?? "?"} → ${r.requested_value}` },
                { key: "status", header: "Status", render: (r) => <StatusPill status={STATUS[r.status].label} tone={STATUS[r.status].tone} /> },
                { key: "case", header: "Case", render: (r) => <CaseLink caseId={r.case_id} /> },
                { key: "by", header: "By", render: (r) => r.requested_by },
                { key: "when", header: "When", render: (r) => <RelativeTime iso={r.requested_at} /> },
                { key: "checked", header: "Checked", render: (r) => <RelativeTime iso={r.last_checked_at} /> },
                {
                  key: "check",
                  header: "",
                  render: (r) =>
                    STATUS[r.status].open ? (
                      <button type="button" onClick={() => check.mutate(r.aws_request_id)} disabled={check.isPending && check.variables === r.aws_request_id} className="inline-flex min-h-8 items-center rounded-lg border border-neutral-300 px-2.5 text-xs font-medium hover:bg-neutral-100 disabled:opacity-50 dark:border-neutral-700 dark:hover:bg-neutral-800">
                        {check.isPending && check.variables === r.aws_request_id ? "Checking…" : "Check status"}
                      </button>
                    ) : null,
                },
              ]}
            />
          )}
        </Card>
      )}
      <Card title="Service Quotas">
        <DataTable
          caption="Service quota limits and current usage"
          rows={q.data.quotas}
          rowKey={(r) => `${r.service_code}#${r.quota_code}`}
          sortBy={(r) => r.quota_name}
          empty={<EmptyState title="No quota snapshot yet" hint="The quota poller runs every six hours." />}
          columns={[
            { key: "name", header: "Quota", render: (r) => <span>{r.quota_name} <span className="ml-1 font-mono text-xs text-neutral-500">{r.service_code}</span></span> },
            { key: "limit", header: "Limit", numeric: true, render: (r) => r.value },
            { key: "usage", header: "Usage", numeric: true, render: (r) => (r.usage === undefined ? "-" : r.usage) },
            { key: "pct", header: "%", numeric: true, render: (r) => (r.usage_percent === undefined ? "-" : <span className={r.usage_percent >= 80 ? "font-semibold text-amber-700 dark:text-amber-300" : ""}>{r.usage_percent.toFixed(0)}%</span>) },
            { key: "source", header: <>Measured by<Hint id="quotas.usage" label="how usage is measured" /></>, render: (r) => <span className="text-xs text-neutral-600 dark:text-neutral-400">{SOURCE_LABEL[r.usage_source] ?? r.usage_source}</span> },
            { key: "observed", header: "As of", render: (r) => <RelativeTime iso={r.observed_at} /> },
            { key: "action", header: "", render: requestButton },
          ]}
        />
      </Card>
    </div>
  );
}

const FormSchema = z.object({ desiredValue: z.coerce.number().positive("Enter a number above the current limit") });

function RequestIncreaseForm({ environmentId, quota, onClose }: { environmentId: string; quota: { serviceCode: string; quotaCode: string; quotaName: string; value: number }; onClose: () => void }) {
  const mutation = useRequestQuotaIncrease(environmentId);
  const [outcome, setOutcome] = useState<QuotaIncreaseRequestOutcome | null>(null);
  // zod 4: a coerced field's input is unknown and its output a number; the form is typed on both.
  const form = useForm<z.input<typeof FormSchema>, unknown, z.output<typeof FormSchema>>({ resolver: zodResolver(FormSchema), defaultValues: { desiredValue: quota.value * 2 } });
  const err = form.formState.errors.desiredValue;
  if (outcome) {
    return (
      <Dialog title={`Request increase: ${quota.quotaName}`} onClose={onClose}>
        <Outcome outcome={outcome} />
        <button type="button" onClick={onClose} className={`${btnPrimary} mt-4`}>
          Close
        </button>
      </Dialog>
    );
  }
  return (
    <Dialog title={`Request increase: ${quota.quotaName}`} onClose={onClose}>
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={form.handleSubmit(async (v) => {
          if (v.desiredValue <= quota.value) {
            form.setError("desiredValue", { message: `Must be above the current limit of ${quota.value}` });
            return;
          }
          setOutcome(await mutation.mutateAsync({ serviceCode: quota.serviceCode, quotaCode: quota.quotaCode, desiredValue: v.desiredValue }));
        })}
      >
        <div>
          <label htmlFor="desiredValue" className="block text-sm font-medium">New limit (current {quota.value})</label>
          <input id="desiredValue" type="number" step="any" aria-describedby={err ? "desiredValue-error" : undefined} aria-invalid={Boolean(err)} {...form.register("desiredValue")} className={inputClass} />
          {err && <p id="desiredValue-error" role="alert" className="mt-1 text-xs text-red-700 dark:text-red-300">{err.message}</p>}
        </div>
        <button type="submit" disabled={mutation.isPending} className={btnPrimary}>
          {mutation.isPending ? "Requesting…" : "Submit request"}
        </button>
        <button type="button" onClick={onClose} className={btnSecondary}>
          Cancel
        </button>
        {mutation.isError && <ErrorState error={mutation.error} />}
      </form>
      <p className="mt-3 text-xs text-neutral-600 dark:text-neutral-400">This assumes the customer's opt-in role and files the request with AWS on their behalf. It is audit-logged.</p>
    </Dialog>
  );
}

/** AWS answers within seconds of filing, and the answer is one of three things the person needs to hear differently. */
function Outcome({ outcome }: { outcome: QuotaIncreaseRequestOutcome }) {
  if (outcome.status === "APPROVED") {
    return (
      <p role="status" className="text-sm">
        <StatusPill status="approved" tone="ok" /> <span className="ml-1">AWS approved it on the spot{outcome.appliedValue !== undefined ? ` - the limit is now ${outcome.appliedValue}` : ""}. The row shows the new limit; usage catches up at the next poll.</span>
      </p>
    );
  }
  if (outcome.status === "CASE_OPENED") {
    return (
      <p role="status" className="text-sm">
        <StatusPill status="case opened" tone="warning" /> <span className="ml-1">AWS opened a Support case{outcome.caseId ? <> (<CaseLink caseId={outcome.caseId} />)</> : ""}. That can take days. The quota's row shows it as open, and Check status asks AWS again.</span>
      </p>
    );
  }
  return (
    <p role="status" className="text-sm">
      <StatusPill status={STATUS[outcome.status].label} tone={STATUS[outcome.status].tone} /> <span className="ml-1">AWS has the request but has not answered yet. The quota's row shows it as requested, and Check status asks AWS again.</span>
    </p>
  );
}
